import React, { createContext, useContext, useState, useEffect } from 'react';
import type { IUser } from '@root/domain/IUser';
import { UserManager, WebStorageStateStore, Log, type User } from 'oidc-client-ts';
import { useApplicationContext } from '@root/providers/application_context.tsx';
import { SessionStorageService } from '@root/shared/services/SessionStorageService';
import { UserProfileCacheService } from '@root/shared/services/UserProfileCacheService';

const DEFAULT_VALUE = {
  user: null,
  accessToken: '',
  initialized: false,
  hasPermissions: false,
  isLoggedIn: false,
  isCheckingAuth: true,
  hasPermission: (_k: string) => false,
  signIn() { return Promise.resolve(); },
  signOut() {},
  signOutCallback() { return Promise.resolve({ returnUrl: '/' }); },
  signInCallback() { return Promise.resolve({ returnUrl: '/' }); },
  signInSilentCallback() { return Promise.resolve({}); },
} as IUserContextType;

export interface ISignoutResponse {
  error?: string;
  returnUrl?: string;
}

export interface ISigninResponse {
  error?: string;
  accessToken?: string;
  returnUrl?: string;
}

export interface IUserContextType {
  user: IUser | null;
  accessToken: string;
  initialized: boolean;
  hasPermissions: boolean;
  isLoggedIn: boolean;
  isCheckingAuth: boolean;
  hasPermission: (permission: string) => boolean;
  signIn(returnUrl?: string): Promise<void>;
  signOut(): void;
  signOutCallback(): Promise<ISignoutResponse>;
  signInCallback(returnUrl?: string): Promise<ISigninResponse>;
  signInSilentCallback(): Promise<ISigninResponse>;
}

export const Permissions = {
  Application: 'applications',
  Vendor: 'vendor-integration',
  IdentityProviders: 'identity-providers',
  Users: 'users',
};

const UserContext = createContext<IUserContextType>(DEFAULT_VALUE);

const signIn = (userManager: UserManager, returnUrl: string = '/'): Promise<void> => {
  return userManager.signinRedirect({
    state: {
      returnUrl: returnUrl,
    },
  });
};

const performOidcSignout = (userManager: UserManager): Promise<void> => {
  return userManager.getUser()
    .then((user) => {
      return userManager.removeUser().then(() => user);
    })
    .then((user) => {
      const oidcKey = `oidc.user:${userManager.settings.authority}:${userManager.settings.client_id}`;
      sessionStorage.removeItem(oidcKey);
      
      return userManager.signoutRedirect({
        id_token_hint: user?.id_token,
        state: { returnUrl: '/' }
      });
    })
    .catch((err) => {
      sessionStorage.clear();
      SessionStorageService.clearAll();
      window.location.href = '/';
      throw err;
    });
};

const isUserLoggedIn = (userManager: UserManager): Promise<boolean> => {
  return userManager.getUser().then((user) => !!user && !user.expired);
};

const signOutCallback = (userManager: UserManager): Promise<ISignoutResponse> => {
  return userManager.signoutCallback().then((signoutResponse) => ({
    returnUrl: (signoutResponse as any)?.returnUrl || '/',
  }));
};

const signInCallback = (userManager: UserManager): Promise<ISigninResponse> => {
  return userManager
    .signinRedirectCallback()
    .then((user?: User) => ({
      returnUrl: (user?.state as any)?.returnUrl || '/',
      accessToken: user?.access_token,
    }))
    .catch((err) => ({
      error: err?.message || err?.toString() || String(err),
    }));
};

const signInSilentCallback = (userManager: UserManager): Promise<ISigninResponse> => {
  return userManager
    .signinSilent()
    .then((user: User | null) => ({
      accessToken: user?.access_token,
    }))
    .catch((err) => ({
      error: err,
    }));
};

const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email) && email.toLowerCase().endsWith('@rxo.com');
};

interface UserContextProviderProps {
  value?: any;
  children: React.ReactNode;
}

export const UserContextProvider = ({ value, children }: UserContextProviderProps): React.ReactElement => {
  const initialValue = {
    ...DEFAULT_VALUE,
    ...value,
  };

  const { config } = useApplicationContext();
  const [user, setUser] = useState<IUser | null>(initialValue.user);
  const [accessToken, setAccessToken] = useState(initialValue.accessToken || SessionStorageService.getToken() || '');
  const [initialized, setIsInitialized] = useState(false);
  const [userManager, setUserMager] = useState<UserManager | null>(initialValue.userManager);
  const [hasPermissions, setHasPermissions] = useState(false);
  const [permissions, setPermissions] = useState<Record<string, boolean>>({});
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const authCheckInProgress = React.useRef(false);
  const hasCheckedAuth = React.useRef(false);

  useEffect(() => {
    const storedToken = SessionStorageService.getToken();
    if (storedToken && !accessToken) {
      setAccessToken(storedToken);
      setIsLoggedIn(true);
      hasCheckedAuth.current = true;
    }
  }, []);

  useEffect(() => {
    const checkAuthStatus = async () => {
      if (authCheckInProgress.current || (hasCheckedAuth.current && accessToken)) {
        return;
      }
      
      if (initialized && userManager && config.initialized) {
        authCheckInProgress.current = true;
        setIsCheckingAuth(true);
        
        try {
          const storedToken = accessToken || SessionStorageService.getToken();
          
          if (storedToken) {
            setIsLoggedIn(true);
            hasCheckedAuth.current = true;
          } else {
            setIsLoggedIn(false);
            hasCheckedAuth.current = true;
          }
        } catch (error) {
          const hasValidToken = !!accessToken || SessionStorageService.hasToken();
          setIsLoggedIn(hasValidToken);
          hasCheckedAuth.current = true;
        } finally {
          setIsCheckingAuth(false);
          authCheckInProgress.current = false;
        }
      } else if (initialized && !userManager) {
        const storedToken = accessToken || SessionStorageService.getToken();
        setIsLoggedIn(!!storedToken);
        setIsCheckingAuth(false);
        hasCheckedAuth.current = true;
      }
    };
    
    checkAuthStatus();
  }, [initialized, userManager, config.initialized]);

  useEffect(() => {
    if (accessToken && config.initialized) {
      fetch(config.authentication.authorityUrl + '/connect/userinfo', {
        headers: { Authorization: 'Bearer ' + accessToken },
      })
      .then((r) => r.json())
      .then((u) => {
        const potentialEmail = u['upn'] || u['email'] || u['sub'];
        
        if (!potentialEmail || !validateEmail(potentialEmail)) {
          setUser(null);
          setAccessToken('');
          SessionStorageService.removeToken();
          sessionStorage.removeItem('userEmail');
          sessionStorage.removeItem('isLoggedIn');
          setIsLoggedIn(false);
          return;
        }
        
        const userEmail = potentialEmail;
        const userName = u['name'] || userEmail;
        
        setUser({
          userId: u['sub'] || u['external_identifier'],
          userName: userEmail,
          firstName: u['given_name'] || userName.split(' ')[0] || '',
          lastName: u['family_name'] || userName.split(' ')[1] || '',
        } as IUser);
        
        if (userEmail) {
          sessionStorage.setItem('userEmail', userEmail);
          sessionStorage.setItem('isLoggedIn', 'true');
          window.dispatchEvent(new Event('userEmailUpdated'));
        }
      })
      .catch(() => {
        setUser(null);
        setAccessToken('');
        SessionStorageService.removeToken();
        sessionStorage.removeItem('userEmail');
        sessionStorage.removeItem('isLoggedIn');
        setIsLoggedIn(false);
      });
    } else {
      setUser(null);
    }
  }, [config, accessToken]);

  useEffect(() => {
    if (config.initialized && userManager == null) {
      const oidcConfig = {
        authority: config.authentication.authorityUrl,
        client_id: config.authentication.clientId,
        redirect_uri: window.location.origin + '/auth',
        response_type: 'code',
        scope: 'openid',
        post_logout_redirect_uri: window.location.origin + '/postsignout',
        userStore: new WebStorageStateStore({ store: window.sessionStorage }),
        automaticSilentRenew: true,
        accessTokenExpiringNotificationTimeInSeconds: 300,
        silent_redirect_uri: window.location.origin + '/silent-renew.html',
        loadUserInfo: true,
        metadata: {
          issuer: config.authentication.authorityUrl,
          authorization_endpoint: `${config.authentication.authorityUrl}/connect/authorize`,
          token_endpoint: `${config.authentication.authorityUrl}/connect/token`,
          userinfo_endpoint: `${config.authentication.authorityUrl}/connect/userinfo`,
          end_session_endpoint: `${config.authentication.authorityUrl}/connect/endsession`,
        }
      };
      
      Log.setLogger(console);
      Log.setLevel(Log.WARN);
      
      if (userManager == null) {
        const manager = new UserManager(oidcConfig);
        
        manager.events.addAccessTokenExpiring(() => {
          console.log('Access token expiring soon, automatic silent renewal will handle refresh...');
          // Don't logout - automaticSilentRenew will refresh the token automatically
        });
        
        manager.events.addAccessTokenExpired(() => {
          console.error('Access token expired - silent renewal may have failed');
          SessionStorageService.clearAll();
          sessionStorage.clear();
          localStorage.removeItem('userEmail');
          localStorage.removeItem('isLoggedIn');
          
          const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
          window.location.href = `${window.location.origin}/?returnUrl=${returnUrl}`;
        });
        
        manager.events.addSilentRenewError((error) => {
          console.error('Silent token renewal failed:', error);
          // Token refresh failed - user will be logged out by addAccessTokenExpired event
        });
        
        manager.events.addUserLoaded((user) => {
          const isLoggingOut = sessionStorage.getItem('is_logging_out');
          if (isLoggingOut === 'true') {
            return;
          }
          
          if (user.access_token) {
            setAccessToken(user.access_token);
            SessionStorageService.setToken(user.access_token);
            
            fetch(config.authentication.authorityUrl + '/connect/userinfo', {
              headers: { Authorization: 'Bearer ' + user.access_token },
            })
            .then((r) => r.json())
            .then((u) => {
              const potentialEmail = u['upn'] || u['email'] || u['sub'];
              
              if (!potentialEmail || !validateEmail(potentialEmail)) {
                setUser(null);
                setAccessToken('');
                SessionStorageService.removeToken();
                sessionStorage.removeItem('userEmail');
                sessionStorage.removeItem('isLoggedIn');
                setIsLoggedIn(false);
                return;
              }
              
              const userEmail = potentialEmail;
              const userName = u['name'] || userEmail;
              
              setUser({
                userId: u['sub'] || u['external_identifier'],
                userName: userEmail,
                firstName: u['given_name'] || userName.split(' ')[0] || '',
                lastName: u['family_name'] || userName.split(' ')[1] || '',
              } as IUser);
              
              if (userEmail) {
                sessionStorage.setItem('userEmail', userEmail);
                sessionStorage.setItem('isLoggedIn', 'true');
                
                window.dispatchEvent(new CustomEvent('userEmailUpdated', { 
                  detail: { forceRefresh: true, email: userEmail } 
                }));
              }
            })
            .catch(() => {});
          }
        });
        
        setUserMager(manager);
        setIsInitialized(true);
      }
    }
  }, [config, userManager]);

  return (
    <UserContext.Provider
      value={{
        user,
        accessToken,
        initialized,
        isLoggedIn,
        isCheckingAuth,
        hasPermissions,
        hasPermission(k: string) {
          return permissions[k] === true;
        },
        signIn(returnUrl?: string) {
          return userManager ? signIn(userManager, returnUrl) : Promise.resolve();
        },
        signOut() {
          if (userManager) {
            sessionStorage.setItem('is_logging_out', 'true');
            SessionStorageService.clearAll();
            UserProfileCacheService.clearCache();
            
            sessionStorage.removeItem('userEmail');
            sessionStorage.removeItem('isLoggedIn');
            sessionStorage.removeItem('oidc_callback_processed');
            localStorage.removeItem('userEmail');
            localStorage.removeItem('isLoggedIn');
            
            setUser(null);
            setAccessToken('');
            setIsLoggedIn(false);
            
            performOidcSignout(userManager)
              .catch(() => {
                sessionStorage.clear();
                SessionStorageService.clearAll();
                sessionStorage.removeItem('is_logging_out');
                window.location.href = '/';
              });
          }
        },
        signOutCallback() {
          return userManager ? signOutCallback(userManager) : Promise.resolve({ returnUrl: '/' });
        },
        signInCallback() {
          return userManager
            ? signInCallback(userManager).then((r) => {
                if (r.accessToken) {
                  setAccessToken(r.accessToken);
                  SessionStorageService.setToken(r.accessToken);
                  setIsLoggedIn(true);
                  hasCheckedAuth.current = true;
                  setIsCheckingAuth(false);
                }
                return r;
              })
            : Promise.resolve({ returnUrl: '/' });
        },
        signInSilentCallback() {
          return userManager
            ? signInSilentCallback(userManager).then((r) => {
                if (r.accessToken) {
                  setAccessToken(r.accessToken);
                  SessionStorageService.setToken(r.accessToken);
                  setIsLoggedIn(true);
                  window.dispatchEvent(new Event('tokenRenewed'));
                }
                return r;
              })
            : Promise.resolve({});
        },
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUserContext = () => {
  return useContext(UserContext);
};
